import React from "react";
import { classNames } from "../utils";
import {
  PlusSmIcon,
  TrashOutlineIcon,
  ClipboardListOutlineIcon,
  XIcon,
} from "./icons";
import { ChevronLeftIcon, PrinterIcon } from "@heroicons/react/24/solid";
import {
  PencilSquareIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import SpinningIcon from "./icons/SpiningIcon";

export enum ButtonColorTypes {
  primary = "primary",
  secondary = "secondary",
  success = "success",
  cancel = "cancel",
  action = "action",
  negativeAction = "negativeAction",
  custom = "custom",
  alternate = "alternate",
  outline = "outline",
  warning = "warning",
}

export enum ButtonTypes {
  default = "default",
  add = "add",
  remove = "remove",
  removeX = "removeX",
  back = "back",
  report = "report",
  saving = "save",
  print = "print",
  spacing = "spacing",
  edit = "edit",
  download = "download",
}

function typeColorToColorClass(type: ButtonColorTypes) {
  switch (type) {
    case ButtonColorTypes.primary:
      return "bg-green-600 text-white";
    case ButtonColorTypes.secondary:
      return "bg-red-500 text-white";
    case ButtonColorTypes.success:
      return "bg-green-600 text-white";
    case ButtonColorTypes.cancel:
      return "bg-white text-gray-600 border";
    case ButtonColorTypes.action:
      return "bg-green-50 text-green-700";
    case ButtonColorTypes.negativeAction:
      return "bg-white text-red-600";
    case ButtonColorTypes.outline:
      return "bg-white border-green-600 border-solid border text-green-600";
    case ButtonColorTypes.warning:
      return "bg-[#FF5D22] text-white";
    case ButtonColorTypes.custom:
    case ButtonColorTypes.alternate:
      return "";
  }
}

function Button({
  text,
  onClick,
  colorType = ButtonColorTypes.primary,
  type = ButtonTypes.default,
  className,
  disabled = false,
  isRoundedFull = false,
  id = undefined,
  textClassName,
  unstyled = false,
  "data-testid": dataTestId,
}: {
  text: string | JSX.Element;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  colorType?: ButtonColorTypes;
  type?: ButtonTypes;
  className?: string;
  disabled?: boolean;
  isRoundedFull?: boolean;
  id?: string;
  textClassName?: string;
  unstyled?: boolean;
  "data-testid"?: string;
}) {
  const baseClass = unstyled
    ? "inline-flex items-center justify-center"
    : "inline-flex justify-center items-center px-4 py-2 cursor-pointer gap-2";

  return (
    <button
      id={id ? id : undefined}
      data-testid={dataTestId}
      className={classNames(
        baseClass,
        isRoundedFull ? "rounded-full" : "",
        colorType === ButtonColorTypes.alternate
          ? "bg-transparent border-2 border-black rounded-full"
          : isRoundedFull
          ? "rounded-full"
          : "rounded-lg",
        className ?? "",
        typeColorToColorClass(colorType),
        disabled ? "opacity-40" : ""
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {type === ButtonTypes.add && (
        <PlusSmIcon data-testid="addSVG" className="w-5" />
      )}

      {type === ButtonTypes.remove && (
        <TrashOutlineIcon data-testid="trashSVG" />
      )}

      {type === ButtonTypes.edit && <PencilSquareIcon className="w-5" />}

      {type === ButtonTypes.print && <PrinterIcon className="w-5" />}
      {type === ButtonTypes.removeX && <XIcon data-testid="xIconSVG" />}
      {type === ButtonTypes.back && <ChevronLeftIcon className="w-7 h-7" />}
      {type === ButtonTypes.report && <ClipboardListOutlineIcon />}
      {type === ButtonTypes.download && (
        <ArrowDownTrayIcon className="w-5 h-5" aria-hidden />
      )}
      {type === ButtonTypes.spacing && <svg className="w-1 h-5"></svg>}
      {text && (
        <label
          className={
            textClassName ?? "text-sm leading-5 font-medium pointer-events-none"
          }
        >
          {text}
        </label>
      )}
      {type === ButtonTypes.spacing && <svg className="w-1 h-5"></svg>}
      {type === ButtonTypes.saving && <SpinningIcon />}
    </button>
  );
}

export default Button;
